import { GridCallbackDetails, GridFilterModel, GridPaginationModel, GridRowId, GridRowParams, GridRowSelectionModel, GridSortModel, MuiEvent } from '@mui/x-data-grid-premium';
import React, { useEffect } from 'react';
import { useConfirmation } from 'src/base/ConfirmationDialog/ConfirmationService';
import { Button, DataGrid, IGridColDef } from 'src/components';
import { translate } from 'src/i18n/tHelper';
import { ApiRequest } from 'src/models/api.request';
import RedPanikHastaListesiResponse from 'src/models/RedPanik/RedPanikHastaListesiResponse';
import { ApiService } from 'src/services/core';
import { useKullaniciPanikRedOkumaKontrolMutation } from 'src/services/RedPanik/RedPanik';
import { useAppDispatch, useAppSelector } from 'src/store';
import { RedPanikSlice } from 'src/store/Slices/RedPanik/RedPanikSlice';

const translatePath = "pages|redPanik";
const RedPanikHastaList = (): JSX.Element => {
    const state = useAppSelector(s => s.redPanik);
    const dispatch = useAppDispatch();
    const confirm = useConfirmation();

    const query = async (req: ApiRequest<RedPanikHastaListesiResponse>) => {
        var service = new ApiService<RedPanikHastaListesiResponse>("RedPanik");
        var res = await service.post("RedPanikHastaListesi", req);
        if (res.responseCode !== 200) {
            confirm({
                description: res.errorMessage,
                variant: "info",
            });
            dispatch(RedPanikSlice.actions.setHastaListData({
                ...res,
                isFetching: false,
                isLoading: false,
            }));
        }
        dispatch(RedPanikSlice.actions.setHastaListData({
            ...res,
            isFetching: false,
            isLoading: false,
        }));
    };

    useEffect(() => {
        (async () => {
            if (!state.hastaListData.isLoading) return;
            dispatch(RedPanikSlice.actions.setHastaListData({
                ...state.hastaListData,
                isFetching: true,
            }));
            let req: ApiRequest<RedPanikHastaListesiResponse> = {
                paging: true,
                pageIndex: state.hastaListPaginationModel.page,
                pageSize: state.hastaListPaginationModel.pageSize,
                filters: { groupOperator: "and", predicates: [] },
                sorts: []
            };
            if (state.hastaListFilterModel && state.hastaListFilterModel.items?.length > 0) {
                state.hastaListFilterModel.items.forEach(filterItem => {
                    const operator = filterItem.operator === "contains" ? "like" :
                        filterItem.operator === "equals" ? "=" :
                            filterItem.operator === "startsWith" ? "startsWith" :
                                filterItem.operator === "endsWith" ? "endsWith" :
                                    filterItem.operator === "isEmpty" ? "empty" :
                                        filterItem.operator === "isNotEmpty" ? "empty" :
                                            "like";

                    req.filters.predicates.push({
                        fieldName: filterItem.field as keyof RedPanikHastaListesiResponse,
                        operator: operator as "endsWith" | "startsWith" | "=" | ">" | ">=" | "<" | "<=" | "like" | "empty" | "in" | "between",
                        value1: filterItem.value,
                        value2: null,
                        not: filterItem.operator === "isNotEmpty" ? true : false,
                    });
                });
            }
            if (state.hastaListSortModel && state.hastaListSortModel?.length > 0) {
                state.hastaListSortModel.forEach(sortItem => {
                    req.sorts.push({
                        fieldName: sortItem.field as keyof RedPanikHastaListesiResponse,
                        ascending: sortItem.sort === 'asc'
                    });
                });
            } else {
                req.sorts.push({ fieldName: "okundu", ascending: false });
                req.sorts.push({ fieldName: "okunacak", ascending: false });
            }
            await query(req);
        })();
    }, [state.hastaListPaginationModel, state.hastaListSortModel, state.hastaListFilterModel, state.hastaListData.isLoading]);

    useEffect(() => {
        if (state.hastaListData?.data?.length > 0 && !state.hastaListData.isFetching)
            if (!state.hastaListActiveRow?.id)
                dispatch(RedPanikSlice.actions.setHastaListActiveRow(state.hastaListData.data?.at(0)));
    }, [state.hastaListData.isFetching]);

    useEffect(() => {
        return () => {
            dispatch(RedPanikSlice.actions.setHastaListData({
                ...state.hastaListData,
                isLoading: true,
                isFetching: true,
            }));
        }
    }, []);

    const columns: IGridColDef<RedPanikHastaListesiResponse>[] = [
        {
            field: "hastaadsoyad",
            headerName: translate(translatePath, "Hasta Ad Soyad"),
            flex: 1,
        },
        {
            field: "okundu",
            headerName: translate(translatePath, "Sayaç"),
            width: 65,
            align: "center",
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                return (
                    <span>{params.row?.okundu || 0} / {params.row?.okunacak || 0}</span>
                )
            }
        },
        {
            field: "okunduBilgi",
            headerName: translate(translatePath, "Okundu"),
            width: 65,
            type: "boolean",
            filterable: false,
            disableColumnMenu: true,
            valueGetter: (params) => { return params.row?.okunduBilgi === "E" },
        },
    ];

    const onPaginationModelChange = (model: GridPaginationModel, details: GridCallbackDetails): void => {
        dispatch(RedPanikSlice.actions.setHastaListPaginationModel(model));
    }
    const onSortModelChange = (model: GridSortModel, details: GridCallbackDetails): void => {
        dispatch(RedPanikSlice.actions.setHastaListSortModel(model));
    }
    const onFilterModelChange = (model: GridFilterModel, details: GridCallbackDetails<"filter">): void => {
        dispatch(RedPanikSlice.actions.setHastaListFilterModel(model));
    }

    const filterData = (data: RedPanikHastaListesiResponse[]): RedPanikHastaListesiResponse[] => {
        if (!Array.isArray(data)) return [];
        return data;
    }
    const [oku, okuState] = useKullaniciPanikRedOkumaKontrolMutation();
    const onRowClick = async (params: GridRowParams<RedPanikHastaListesiResponse>, event: MuiEvent<React.MouseEvent<HTMLElement, MouseEvent>>, details: GridCallbackDetails) => {
        dispatch(RedPanikSlice.actions.setIslemListData({
            ...state.islemListData,
            data: [],
            isFetching: true,
            isLoading: true,
        }));
        if (params.row.okunduBilgi === "H") {
            await oku({ hastaKimlikId: params.row.id }).unwrap().then((v) => {
                if (v) {
                    dispatch(RedPanikSlice.actions.setHastaListData({
                        ...state.hastaListData,
                        data: state.hastaListData.data?.map(m => {
                            if (m.id === params.row.id) {
                                return {
                                    ...m,
                                    okunduBilgi: "E",
                                    okundu: m.okunacak,
                                }
                            }
                            return m;
                        }),
                        // isLoading: true,
                        // isFetching: true,
                    }));
                }
            });
        }
        dispatch(RedPanikSlice.actions.setHastaListActiveRow(params.row));
    }

    const handlerSearch = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        dispatch(RedPanikSlice.actions.setHastaListActiveRow(new RedPanikHastaListesiResponse()));
        dispatch(RedPanikSlice.actions.setHastaListData({
            ...state.hastaListData,
            isLoading: true,
            isFetching: true,
        }));
    }
    return (
        <DataGrid
            unstable_headerFilters
            hiddenColumnsButton
            hideFooterSelectedRowCount
            hiddenExport
            hiddenFilters
            searchButton={<Button customType="search" onClick={handlerSearch} />}
            sx={{ height: "60vh" }}
            rows={filterData(state.hastaListData?.data) || []}
            columns={columns}
            loading={state.hastaListData.isFetching || okuState.isLoading}
            rowCount={state.hastaListData?.totalRecords || 0}
            rowSelectionModel={state.hastaListActiveRow ? [state.hastaListActiveRow.id] : []}
            onRowClick={onRowClick}
            pagination={true}
            paginationMode="server"
            sortingMode="server"
            filterMode="server"
            paginationModel={state.hastaListPaginationModel}
            onPaginationModelChange={onPaginationModelChange}
            sortModel={state.hastaListSortModel}
            onSortModelChange={onSortModelChange}
            filterModel={state.hastaListFilterModel}
            onFilterModelChange={onFilterModelChange}
            slots={{
                headerFilterMenu: null
            }}
        />
    );
};

export default RedPanikHastaList;